import {
  addDays,
  addMonthsWib,
  dayKey,
  monthKey,
  startOfDayWib,
  startOfMonthWib,
  startOfWeekWib,
  wibDate,
  wibParts,
} from './wib';

export const PERIOD_PRESETS = ['today', 'week', 'month', 'last-month', '30d', '90d', 'year'] as const;
export type PeriodPreset = (typeof PERIOD_PRESETS)[number];

export const GRANULARITIES = ['day', 'week', 'month'] as const;
export type Granularity = (typeof GRANULARITIES)[number];

export interface PeriodRange {
  readonly from: Date;
  readonly to: Date;
  readonly previousFrom: Date;
  readonly previousTo: Date;
  readonly granularity: Granularity;
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function granularityFor(from: Date, to: Date): Granularity {
  const days = (to.getTime() - from.getTime()) / DAY_MS;
  if (days <= 45) return 'day';
  if (days <= 120) return 'week';
  return 'month';
}

function shifted(from: Date, to: Date, previousFrom: Date, granularity?: Granularity): PeriodRange {
  return { from, to, previousFrom, previousTo: from, granularity: granularity ?? granularityFor(from, to) };
}

export function customPeriod(from: Date, to: Date): PeriodRange {
  const length = to.getTime() - from.getTime();
  return shifted(from, to, new Date(from.getTime() - length));
}

export function resolvePeriod(preset: PeriodPreset, now: Date): PeriodRange {
  switch (preset) {
    case 'today': {
      const from = startOfDayWib(now);
      return shifted(from, addDays(from, 1), addDays(from, -1), 'day');
    }
    case 'week': {
      const from = startOfWeekWib(now);
      return shifted(from, addDays(from, 7), addDays(from, -7), 'day');
    }
    case 'month': {
      const from = startOfMonthWib(now);
      return shifted(from, addMonthsWib(from, 1), addMonthsWib(from, -1), 'day');
    }
    case 'last-month': {
      const from = addMonthsWib(startOfMonthWib(now), -1);
      return shifted(from, addMonthsWib(from, 1), addMonthsWib(from, -1), 'day');
    }
    case '30d':
    case '90d': {
      const days = preset === '30d' ? 30 : 90;
      const to = addDays(startOfDayWib(now), 1);
      const from = addDays(to, -days);
      return shifted(from, to, addDays(from, -days));
    }
    case 'year': {
      const { year } = wibParts(now);
      const from = wibDate(year, 1);
      return shifted(from, wibDate(year + 1, 1), wibDate(year - 1, 1), 'month');
    }
  }
}

function bucketStart(date: Date, granularity: Granularity): Date {
  if (granularity === 'day') return startOfDayWib(date);
  if (granularity === 'week') return startOfWeekWib(date);
  return startOfMonthWib(date);
}

export function bucketKeyOf(date: Date, granularity: Granularity): string {
  return granularity === 'month' ? monthKey(date) : dayKey(bucketStart(date, granularity));
}

export function bucketKeys(from: Date, to: Date, granularity: Granularity): string[] {
  const keys: string[] = [];
  let cursor = bucketStart(from, granularity);
  while (cursor < to) {
    keys.push(bucketKeyOf(cursor, granularity));
    cursor =
      granularity === 'day'
        ? addDays(cursor, 1)
        : granularity === 'week'
          ? addDays(cursor, 7)
          : addMonthsWib(cursor, 1);
  }
  return keys;
}
